let _ = require('../_.js');
var $ = jQuery;

/**
 * _.Modal.js
 * 
 * This script contains a _.Modal class that can be used to display modals with customizable content, including title, body and buttons.
 * 
 * It provides the following functionalities:
 * 
 * _.Modal.clone():
 * - Clones the template modal and returns the cloned jQuery element.
 * 
 * _.Modal.hide():
 * - Closes the currently visible modal. Any ongoing promise will be resolved with "false".
 * 
 * _.Modal.prompt(options):
 * - Displays a prompt modal. Takes an object as argument with optional properties: title, body and input.
 * - The input property should be a jQuery object of an input element.
 * - Returns a promise which resolves to the value of the input when confirmed, and "false" otherwise.
 * 
 * _.Modal.confirm(options):
 * - Displays a confirmation modal. Takes an object as argument with optional properties: title and body.
 * - Returns a promise which resolves to "true" when confirmed, and "false" otherwise.
 * 
 * _.Modal.new(options):
 * - Opens a new modal. Takes an object as argument with optional properties: title, body and buttons.
 * - The buttons property should be an array of button objects, where each button object has the properties: title, class, callback and default.
 * - Returns a promise which resolves to the value returned by the callback of the clicked button, and "false" otherwise.
 *
 * _.Modal.form(options, async callback):
 * *** See full example below for details. ***
 * - Opens a form modal. Takes an object as argument with properties defining the form.  
 * - The fields property should be a jQuery object of the form fields.
 * - The validate property should be a function that takes the form data as argument and returns a boolean indicating the validity of the data.
 * - If callback is omitted, the form values will simply be returned when submit is clicked, and the modal closed.
 * - If passed a callback, the code in the callback will execute on submit.  If the callback returns any value, the form promise will 
 *   resolve and return whatever the return value from the callback is.  This is useful for, say, showing a form, performing an ajax function,
 *   and then either showing an error or returning a response from the server when the modal is submitted.  
 
 * jQuery.fn.modal_show(title, body, buttons):
 * - This allows the most control over how a modal looks and behaves.  The other 4 functions above are abstractions that create a modal and call this.
 * - jQuery method that can be used on a jQuery modal element div to display it with given title, body and buttons.
 * - The buttons argument is an array of objects, each having properties: title, class, callback, and optionally default.
 * - If default is set on a button, pressing return will trigger its callback. The returned value of the callback is used to resolve an associated promise.
 * - This method also takes care of showing the modal overlay.
 * 
 * jQuery.fn.modal_close():
 * - jQuery method that can be used on a jQuery modal element to hide it.
 * 
 * Some basic examples to demonstrate usage:
 *   Create a new model with custom header and body
 *   _.Modal.clone().modal_show("Hello", "World!");
 *   console.log("Confirm: ", await _.Modal.confirm("Hello", "World!"));
 *   console.log("Prompt: ", await _.Modal.prompt("Hello", "World!"));
 *   _.Modal.new({ title: "Hello", body: "World!" });
 *
 * Note that in most cases validate => () is optional and unnecessary, as server side validation has same effect
 * 
 * Form example.  
 * 
    $(this).click(async function () {
        // Pre fade in overlay
        $('#lp-modal-overlay').show();

        // Get registrants data
        let auction_registrations = _.clerk_state.auction.auction_registrations;

        // Prepare select field
        let bidderSelect = $('<select class="selectpicker form-control bidder-select" name="bidder" data-live-search="true" title="Bidder" style="width:350px;"></select>');
        bidderSelect.append('<option value="floor">Floor</option>')
        console.log("auction_registrations", auction_registrations)

        $.each(auction_registrations, function (index, registration) {
            bidderSelect.append('<option value="' + registration.registrant.id + '">' + _.full_name(registration.registrant) + (registration.registrant.email ? ' (' + registration.registrant.email + ')' : '') + '</option>')
        });

        // Prepare input field for the amount
        let amountInput = $('<div class="input-group"><span class="input-group-text">$</span><input type="text" class="form-control" name="amount" /></div>');

        // Prepare the 'Floor' button
        let floorButton = $('<button type="button" class="btn btn-primary btn-sm">Floor</button>');
        floorButton.on('click', function () {
            bidderSelect.selectpicker('val', 'floor');
        });

        // Prepare the input group for the bidder select
        let bidderInputGroup = $('<div class="input-group"></div>');
        bidderInputGroup.append(bidderSelect).append(floorButton);

        // Create form fields
        const fields = {
            "Bidder": bidderInputGroup,
            "Amount": amountInput
        };

        // Setup modal form options
        const options = {
            title: 'New Bid - ' + _.clerk_state.current_item.title,
            body: 'Please select a bidder an an amount:',
            fields: fields,
            body_width: 400,
            hide_on_submit: false,
            submitCaption: 'Submit Bid',
            cancelCaption: 'Cancel',
            validate: function (data) {
                let errors = {};
                if (!data.bidder) {
                    errors.bidder = 'Please select a bidder.';
                }
                if (!data.amount || isNaN(data.amount)) {
                    errors.amount = 'Please provide a valid bid amount.';
                }
                return $.isEmptyObject(errors) ? true : errors;
            }
        };

        // Display the form and process result
        // (await being used here keeps realtime data updates from happening while the form is open - 
        //      .click() locks realtime updates, and until the form closes, .click() does not resolve)
        await _.Modal.form(options, async function (result) {

            let bidder = result.bidder;
            let amount = result.amount;

            try {
                const response = await _.ajax({
                    url: '/api/v1/auction_items/' + _.clerk_state.current_item_id + '/clerk/ajax/create_bid',
                    type: 'POST',
                    data: {
                        'bidder': bidder,
                        'amount': amount,
                        '_token': $("#csrf").val()
                    }
                });

                // Success, close modal.
                console.log("Added bid", bidder, amount);

                return true;

            } catch (error) {
                // Server side validation / general error, show error and keep modal open
                result = await _.Modal.form_reopen_error(error);
            }
        });
    });
 * 
 */

// This code is such a mess, sorry.  Refactoring takes time, hacking and mashing crap together works now.

let _was_mobile_init = false;
let _was_desktop_init = false;
let _modal_scroll_locked = false;
let all_hide_callbacks = [];
let $current_modal = null;
let unhidable_active = false;

// Page scroll lock while a modal is open. overflow:hidden on the root element stops document
// scrolling (desktop and iOS 16+) without moving the page, so scroll position and anything
// positioned against the page (auctioncast player) are untouched. scrollbar-gutter keeps the
// layout width stable when a classic scrollbar was showing. Idempotent via _modal_scroll_locked.
function _modal_scroll_lock() {
    if (_modal_scroll_locked) return;
    _modal_scroll_locked = true;
    let root = document.documentElement;
    if (window.innerWidth > root.clientWidth) {
        root.style.scrollbarGutter = 'stable';
    }
    root.style.overflow = 'hidden';
}

function _modal_scroll_unlock() {
    if (!_modal_scroll_locked) return;
    _modal_scroll_locked = false;
    let root = document.documentElement;
    root.style.overflow = '';
    root.style.scrollbarGutter = '';
}

(function ($) {
    $.fn.modal_show = async function ({ title = null, body = null, buttons = [], body_width = null, hide_on_submit = false, closable = true, close_existing = false } = {}) {

        let $that = this;

        if (!close_existing || unhidable_active) {
            while (
                // $('#lp-modal-overlay').is(":visible") &&
                ($current_modal && $current_modal.is(":visible") && (
                    (!$current_modal.attr('id') && $that.attr('id')) ||
                    ($current_modal.attr('id') && !$that.attr('id')) ||
                    ($current_modal.attr('id') && $that.attr('id') && $current_modal.attr('id') != $that.attr('id')
                    )
                ))
            ) {
                await _.sleep(0.1);
            }
        }

        $current_modal = $that;

        $('#lp-modal-overlay').stop().show();
        await _.Modal.hide({ hide_overlay: false });

        this.css('opacity', '0.01');
        this.show();

        if (this.style('min-height')) {
            let $child_card = this.find('.card').first();
            if ($child_card.length) {
                if ($child_card.style('min-height')) {
                    if (!$child_card.styleInt('min-height') || $child_card.styleInt('min-height') < this.styleInt('min-height')) {
                        $child_card.styleInt(this.styleInt('min-height'));
                    }
                }
            }
        }

        _modal_scroll_lock();

        $("body").prepend($('#lp-modal-overlay'));
        $('body').prepend($(this));

        if (title) {
            let titleElement = typeof title === 'string' ? $(document.createTextNode(title)) : title;
            this.find('.modal-title').empty().append(titleElement);
        }

        if (body) {
            let bodyElement;
            if (typeof body === 'string') {
                const escapedBody = $('<div>').text(body).html().replace(/\n/g, '<br />');
                bodyElement = $(document.createElement('div')).html(escapedBody);
            } else {
                bodyElement = body;
            }
            this.find('.modal-body').empty().append(bodyElement);
            this.find('.modal-body').show();
        } else {
            // Hide modal-body container when no body content
            this.find('.modal-body').hide();
        }

        if (body_width) {
            this.find('.modal-body').children().css("width", body_width + "px");
        }

        this.find('.modal-buttons').empty();
        this.find('.async-pleasewait').each(function () {
            $(this).removeClass('async-pleasewait');
        });

        if (closable) {
            $(".btn.modal-close").show();
        } else {
            $(".btn.modal-close").hide();
        }

        if (buttons.length > 0) {
            let buttonDiv = this.find('.modal-buttons');
            for (let i = 0; i < buttons.length; i++) {
                let button = $('<button/>', {
                    type: 'button',
                    class: 'btn ' + buttons[i].class,
                    html: buttons[i].title,
                });

                let click_handler = async function () {
                    let result = await buttons[i].callback();

                    // If any result is made from callback
                    if (result !== undefined) {

                        // deferred is the 'return' value of this modal.  If something is returned, the modal
                        // will close and resolve.
                        let deferred = $that.data('deferred');

                        if (deferred) {
                            deferred.resolve(result);
                            $that.removeData('deferred');
                        }

                        await _.Modal.hide();
                    }
                };

                // Add click handler to button.  Use pleasewait if btn-success.
                // console.log(buttons[i].class);
                // console.log($button);
                if (buttons[i].class == "btn-success") {
                    $(button).click_pleasewait(click_handler);
                } else {
                    $(button).click(click_handler);
                }

                if (buttons[i].default) {
                    _.Modal._success_callback = buttons[i].callback;
                }
                buttonDiv.append(button);
            }
        }
        // updateModalMobileSizing will handle setting approproate scroll lock styles.  This reset's the state.

        let return_props = {
            // Pass a function to have that function execute when _.Modal.hide(); is called.
            on_close: function (fn) {
                // Hijacks 'differed' and overrides the modal so on close, it calls this rather than resolves the promise.
                // This method will only be accessible if the developer calls $().show_modal() directly anyway, so this 
                // won't affect any of the methods below such as prompt() or form().
                $(this).data('deferred', { resolve: fn });
            }
        };

        this.css('opacity', '0.01').show();

        await _.ui_update();
        await _.sleep(10);

        let that = this;

        setTimeout(function () {
            that.css('opacity', '1');
        }, 75);

        // Make extra sure the overlay is there
        $('#lp-modal-overlay').stop().show();

        // Fade in the modal
        // await this.aFadeIn();
        this.show();
        this.css('opacity', '1');

        // Check for overlay again
        $('#lp-modal-overlay').stop().show();

        await _.ui_update();

        // Hacks, I would rather not have to do this...
        // window.dispatchEvent(new Event('resize'));

        return return_props;
    };

    $.fn.modal_close = function () {
        _.Modal.hide();
        return this;
    };
}(jQuery));


var _modal_update_responsive_styling = function () {

    let $modal = $(".lp-modal:visible");

    if (!$modal.length) {
        $('.page-content').css({
            'overflow': 'auto'
        });

    } else {
        $('.page-content').css({
            'overflow': 'hidden'
        });

        // --- Code for adjusting heights for zoom.
        //  We are using the zoom: property as a hack to make the modals smaller on mobile devices.  But this means
        //  setting the height of the modal to the height of the window will not actually be full height because
        //  the zoom makes the modal smaller.  We have to divide by the zoom ratio to make the modal appropriatly taller.
        function supportsZoom() {
            if (!_.is_frontend) return false;
            var div = document.createElement('div');
            return 'zoom' in div.style;
        }

        function getZoomApplied() {
            if (!supportsZoom()) return 1;
            let width = $(window).width();
            if (width <= 420) return .75;
            if (width <= 600) return .8;
            if (width <= 1000) return .9;
            return 1;
        }
        // --- End adjust for zoom

        // Check to see if we are in a constrained display size.  If the content height is greater than 90% of the height of
        // the display, make the modal full height (well, nearly full height, 98%)
        let content_height = $modal.outerHeight();

        content_height = content_height / getZoomApplied();

        if ((content_height + 50 > $(window).height() || content_height > $(window).height() * .9)) {
            // Full height modal.
            let target_height = $(window).height() * .98;

            target_height = target_height / getZoomApplied();

            // Apply the full height style.
            $modal.css({
                // 'overflow-x': 'hidden',  // Test me in all scenarios
                'height': target_height + 'px',
                'max-width': (parseInt(94 / getZoomApplied()) + "vw"),
                // 'min-width': (parseInt(94 / getZoomApplied()) + "vw"),
            });

            $modal.find(".ac-card-body").css({
                'overflow-y': 'auto',
            })

            // --- Apply a rule which spaces out content marked for spacing out if conditions are met:
            //     - Full height modal (if we are in this code block this is true)
            //     - Contained content does not overflow the parent area (checking if height < 95%)
            $modal.find('.lp-row-mobile-spread-out-content').each(function () {
                var $parent = $(this).parent();

                $(this).removeClass('lp-row-mobile-spread-out-content-active');

                var parentHeight = $parent.closest('.lp-modal').innerHeight();
                var contentHeight = $(this).outerHeight();

                // Calculate the 95% height threshold, remove 100 to adjust for modal header and body margins.
                // Yes, this isn't an exact science...
                var thresholdHeight = (parentHeight - 100) * 0.95;

                // Compare and apply/remove classes accordingly
                if (contentHeight < thresholdHeight) {
                    $(this).addClass('lp-row-mobile-spread-out-content-active');
                }

                // 
            });
            // --- End space out content
        } else {
            $modal.css({
                'overflow-y': 'unset',
                'overflow-x': 'unset',
                'height': '',
                'max-width': (parseInt(94 / getZoomApplied()) + "vw"),
            });

            $modal.find(".ac-card-body").css({
                'overflow-y': 'unset',
            })

            // Remove spread out content if active styles.
            $modal.find(".lp-row-mobile-spread-out-content").removeClass("lp-row-mobile-spread-out-content-active");
        }

        // I removed this:
        // 'min-width': (parseInt(94 / getZoomApplied()) + "vw"),
        // Lets see how it works.
    }
}

setInterval(_modal_update_responsive_styling, 300);

// Interface and callable functions
_.Modal = class Modal {
    static _init() {
        // Handle modal close button (include children for icon clicks)
        let closeableSelector = '.modal-close, .modal-close *';

        if (window.location.href.indexOf('https://ace.landproz') != -1) {
            closeableSelector += ", #lp-modal-overlay";
        }

        $(document).on('click', closeableSelector, function () {
            if ($('.lp-modal:visible').length && $('.lp-modal:visible .btn.modal-close').is(":visible")) {
                _.Modal.hide();
            }
        });

        // On interval, hide background if no modal displayed
        setInterval(async function () {
            if ($('.lp-modal:visible').length && !$('#lp-modal-overlay:visible').length) {
                if (_._modal_is_hiding) {
                    await _.sleep(2000);
                }
                if (!$('#lp-modal-overlay:visible').length) {
                    await $('#lp-modal-overlay').show();
                }
            } else if (!$('.lp-modal:visible').length && $('#lp-modal-overlay:visible').length) {
                if (_._modal_is_hiding) {
                    await _.sleep(2000);
                }
                if ($('#lp-modal-overlay:visible').length) {
                    $(".lp-modal").css('height', '');
                    await $('#lp-modal-overlay').hide();
                }
            }
        }, 2000);

        // Safety net for modals dismissed outside hide() (element hidden/removed directly): if the
        // scroll lock is held but no modal has been visible for 3 consecutive 1s checks (> 2s),
        // release the lock and hide the overlay.
        let lock_idle_checks = 0;
        setInterval(function () {
            if (!_modal_scroll_locked || $('.lp-modal:visible').length) {
                lock_idle_checks = 0;
                return;
            }
            lock_idle_checks++;
            if (lock_idle_checks >= 3) {
                lock_idle_checks = 0;
                _modal_scroll_unlock();
                $('#lp-modal-overlay').hide();
            }
        }, 1000);

        // Handle escape key (Enter key submission disabled for better UX)
        $(document).on('keyup', function (e) {
            if (e.key == "Escape") {
                if ($('.lp-modal:visible').length && $('.lp-modal:visible .btn.modal-close').is(":visible")) {
                    _.Modal.hide();
                }
            }
        });
    }

    static clone() {
        $('.lp-modal:visible').each(function () {
            _.Modal.hide();
        });

        $('#modal-clone').remove();
        let clone = $('#modal-template').clone().attr('id', 'modal-clone').hide();
        $('body').append(clone);
        return clone;
    }

    // Used on frontend to check for notifications after a modal hides.
    static register_callback_on_all_hide_operations(callback) {
        all_hide_callbacks.push(callback);
    }

    // Set to true to block the hide() function from working.  Used for choice finalization
    // screens, these can't just dissapear on the user.
    static set_unhidable() {
        unhidable_active = true;
    }

    // Hides a modal which was set to set_unhidable.
    // Accepts optional options object (e.g. { hide_overlay: false }) passed through to hide().
    static close_unhidable(options = null) {
        unhidable_active = false;
        return _.Modal.hide(options);
    }

    static hide(options = null) {
        if (unhidable_active) return;
        if (options === null) {
            options = { hide_overlay: true };
        }
        return new Promise(function (resolve, reject) {

            $(".lp-modal").css('height', '');

            // Get whatever is visibles 
            let $visibleModal = $('.lp-modal:visible');
            let $visibleOverlay = $('#lp-modal-overlay:visible');

            // hide_overlay:false means another modal is about to open (modal_show pre-hide, chained
            // notifications) - keep the scroll lock so it is not released and re-taken.
            if (options.hide_overlay !== false) {
                _modal_scroll_unlock();
            }

            // Do nothing if no modal is visible
            // if (!$visibleModal.length && !$visibleOverlay.length) {
            //     resolve();
            //     return;
            // }

            // console.log("start hide");
            if (_._modal_is_hiding) {
                _._modal_hide_callbacks.push(resolve);
                return;
            }

            // Replace overlay with dummy object if the function is being told to not hide overlay
            if (options.hide_overlay === false) {
                $visibleOverlay = $("<div />"); // Dummy
            }

            if (!$visibleModal.length && !$visibleOverlay.length) {
                resolve();
                return;
            }

            _._modal_is_hiding = true;
            _._modal_hide_callbacks = [resolve];

            (async function () {
                // console.warn("Hide");

                // await Promise.all([
                //     $visibleModal.aFadeOut(300),
                //     $visibleOverlay.aFadeOut(300)
                // ]);

                $visibleModal.hide();
                $visibleOverlay.hide();

                $(".lp-modal").css('height', '');

                $visibleModal.each(function () {
                    let deferred = $(this).data('deferred');
                    if (deferred) {
                        $(this).removeData('deferred');
                        deferred.resolve(false);
                    }
                });

                let modal_hide_callbacks = _._modal_hide_callbacks;
                _._modal_hide_callbacks = [];
                _._modal_is_hiding = false;
                // console.log("hide finished");

                $current_modal = null;

                for (let i = 0; i < modal_hide_callbacks.length; i++) {
                    modal_hide_callbacks[i]();
                }

                for (let i = 0; i < all_hide_callbacks.length; i++) {
                    all_hide_callbacks[i]();
                }
            })();

        });
    }

    static async alert(title_or_body = null, body = null, button_label = "OK", body_width = null) {
        let options = {
            title: 'Notice',
            body: 'Thought you should know.',
            body_width: null
        }

        // title becomes body if only it is provided
        let title;

        if (title_or_body !== null && body === null) {
            body = title_or_body;
            title = null;
        } else {
            title = title_or_body;
        }

        if (title) options.title = title;
        if (body) options.body = body;
        if (body_width) options.body_width = body_width;

        let buttons = [
            {
                title: button_label,
                class: "btn-success",
                callback: () => true,
                default: true
            }
        ];

        options.buttons = buttons;

        let clone = this.clone();
        let deferred = $.Deferred();
        clone.data('deferred', deferred);

        await clone.modal_show(options);

        return deferred.promise();
    }

    // Shows a prompt, takes title and body as minimum arguments.  
    // Returns a string, or false if the user clicked cancel.
    static prompt(options) {
        let $theInput;

        if (options.textarea) {
            let height = options.textarea_height ? options.textarea_height : 150;
            $theInput = $('<textarea class="form-control MB-2" style="height:' + height + 'px; min-height:' + height + 'px" ></textarea>');
        } else {
            $theInput = $('<input type="text" class="form-control MB-2" style="max-height:40px" />');
        }

        options = Object.assign({
            title: 'Prompt',
            body: 'Please enter your value:',
            input: $theInput,
            body_width: null,
            default: "",
            center_fields: false
        }, options);

        // Set default value
        options.input.val(options.default);

        let buttons = [{
            title: "Cancel",
            class: "btn-secondary",
            callback: () => false,
            default: false
        },
        {
            title: "Submit",
            class: "btn-primary",
            callback: () => options.input.val(),
            default: true
        }
        ];

        let clone = this.clone();
        let deferred = $.Deferred();
        clone.data('deferred', deferred);

        let body = $('<div class="mb-1">' + options.body + '</div>').add(options.input);
        if (options.center_fields) {
            body.addClass(options.center_fields ? 'form-elements-align-center' : 'form-elements-align-left');
        }

        clone.modal_show({
            title: options.title,
            body: body,
            buttons: buttons,
            body_width: options.body_width
        });

        setTimeout(function () {
            $theInput.select();
        }, 500);

        return deferred.promise();
    }

    // Shows an alert, but for error messages.  Should be able to handle every kind of error message format.
    // See _.flash_error()
    static async alert_error(errors, title, header_title = "An error has occurred") {
        if (errors && errors.hasOwnProperty && errors.hasOwnProperty('responseJSON')) {
            errors.error = errors.responseJSON;
            delete errors.errors;
        }

        if (errors && errors.hasOwnProperty && errors.hasOwnProperty('errors')) {
            errors.error = errors.errors;
            delete errors.errors;
        }

        if (errors && errors.hasOwnProperty && errors.hasOwnProperty('error')) {
            errors = errors.error;

            // Convert from laravel blade validator syntax
            // let formattedErrors = {};
            // for (const field in errors) {
            //     _.Modal.alert_error(errors[field][0], 'danger', 'end');
            // }
            // return;

            if (errors.return) {
                _.Modal.alert_error(errors.return);
                return;
            } else {

                // Just display first error
                for (const field in errors) {
                    _.Modal.alert_error(errors[field][0]);
                    return;
                }
            }
        }

        if (errors && typeof (errors) == "string") {
            errors = { message: errors };
        }

        console.warn(errors);

        let messages = [];

        if (errors && errors.message) {
            messages.push(errors.message);
        } else if (errors && typeof (errors) === 'string') {
            messages.push(errors);
            _.Modal.alert_error(errors, 'danger', 'end');
        } else if (errors && typeof (errors) == "Array") {
            for (const key in errors) {
                messages.push(errors[key]);

                _.Modal.alert_error(errors[key], 'danger', 'end');
            }
        } else {
            messages.push("Unknown error: " + JSON.stringify(errors));
        }

        let $message_formatted = $("<div />")
        $message_formatted.append($("<div />").text(title).css({ "margin-bottom": "30px", "font-weight": "bold" }));
        for (let i in messages) {
            $message_formatted.append($("<div />").text(messages[i]).prepend('<i class="fa-solid fa-circle-exclamation" style="padding-right: 15px; color:darkred;"></i>'));
        }
        $message_formatted.append($("<div />").css({ "height": "30px" }));

        return this.alert(header_title, $message_formatted);
    }

    // Useful for showing something like please wait
    // Waiting this function just waits for the modal to appear. 
    static async unclosable(title_or_body = null, body = null, body_width = null) {
        let options = {
            title: 'Notice',
            body: 'Please Wait...',
            body_width: null
        }

        // title becomes body if only it is provided
        let title;

        if (title_or_body !== null && body === null) {
            body = title_or_body;
            title = null;
        } else {
            title = title_or_body;
        }

        // Update an existing modal if it is already visible
        if ($("#modal-clone:visible").length) {
            $("#modal-clone .modal-title").text(title);
            $("#modal-clone .modal-body > div").text(body);
            return;
        }

        if (title) options.title = title;
        if (body) options.body = body;
        if (body_width) options.body_width = body_width;

        let buttons = [];

        let clone = this.clone();
        let deferred = $.Deferred();
        clone.data('deferred', deferred);

        await clone.modal_show({
            title: options.title,
            body: options.body,
            buttons: buttons,
            body_width: options.body_width,
            hide_on_submit: false,
            closable: false
        });

        return;
    }

    static confirm(title_or_body = null, body = null, body_width = null, cancel_option_label = null, confirm_option_label = null) {
        let options = {
            title: 'Confirm Action',
            body: 'Are you sure?',
            body_width: null,
            cancel_option_label: "Cancel",
            confirm_option_label: "Confirm"
        }

        // title becomes body if only it is provided
        let title;

        if (title_or_body !== null && body === null) {
            body = title_or_body;
            title = null;
        } else {
            title = title_or_body;
        }

        if (title) options.title = title;
        if (body) options.body = body;
        if (body_width) options.body_width = body_width;
        if (cancel_option_label) options.cancel_option_label = cancel_option_label;
        if (confirm_option_label) options.confirm_option_label = confirm_option_label;

        let buttons = [{
            title: options.cancel_option_label,
            class: "btn-secondary",
            callback: () => false,
            default: false
        },
        {
            title: options.confirm_option_label,
            class: "btn-success",
            callback: () => true,
            default: true
        }
        ];

        options.buttons = buttons;

        let clone = this.clone();
        let deferred = $.Deferred();
        clone.data('deferred', deferred);

        clone.modal_show(options);

        return deferred.promise();
    }

    // You may provide your own buttons here.  Buttons should have property 'value'
    // which will be what gets returned from this modal if selected.
    static pick(title_or_body = null, body = null, body_width = null, buttons) {
        let options = {
            title: 'Confirm Action',
            body: 'Are you sure?',
            body_width: null,
            buttons: [{
                title: "Cancel",
                class: "btn-secondary",
                value: "cancel",
                default: false
            },
            {
                title: "Confirm",
                class: "btn-success",
                value: "confirm",
                default: true
            }]
        }

        // title becomes body if only it is provided
        let title;

        if (title_or_body !== null && body === null) {
            body = title_or_body;
            title = null;
        } else {
            title = title_or_body;
        }

        if (title) options.title = title;
        if (body) options.body = body;
        if (body_width) options.body_width = body_width;

        for (let i in buttons) {
            buttons[i].callback = function () {
                return buttons[i].value;
            }
        }

        options.buttons = buttons;

        let clone = this.clone();
        let deferred = $.Deferred();
        clone.data('deferred', deferred);

        clone.modal_show(options);

        return deferred.promise();
    }

    static new(options) {
        options = Object.assign({
            title: 'New _.Modal',
            body: '_.Modal content goes here',
            buttons: [],
            body_width: null
        }, options);

        let clone = this.clone();
        let deferred = $.Deferred();
        clone.data('deferred', deferred);

        clone.modal_show(options);

        return deferred.promise();
    }

    static form(options, callback = null) {
        if (callback === null) {
            callback = async function (v) { return v; }
        }

        options = Object.assign({
            title: 'Form',
            body: 'Please fill out the form:',
            body_width: null,
            fields: {},
            validate: () => true,
            cancelCaption: "Cancel",
            submitCaption: "Submit",
            additionalButtons: [],
            buttons: [],
            hide_on_submit: true,
            center_fields: false,
            on_show: function () { }
        }, options);

        // Place Cancel button at first position
        options.buttons.push({
            title: options.cancelCaption,
            class: "btn-secondary",
            callback: function () {
                _.Modal.hide();
                return null;
            }
        });

        // Insert any additional buttons
        options.additionalButtons.forEach(button => {
            options.buttons.push(button);
        });

        // Place Submit button at last position
        options.buttons.push({
            title: options.submitCaption,
            class: "btn-success",
            callback: async function () {
                _.reset_form_errors('.modal-body');

                let fieldValues = {};

                // Collect all field values
                for (let field in options.fields) {
                    let $fieldElements = $(options.fields[field]);

                    $fieldElements.each(function () {
                        let $this = $(this);

                        if ($this.hasClass("form-control") && $this.attr('name')) {
                            let fieldName = $this.attr('name');
                            // console.log(fieldName);
                            if (!fieldName) return true; // Continue to the next iteration
                            // console.log($this.val());
                            fieldValues[fieldName] = $this.val();
                        } else {
                            $this.find(".form-control[name!=''], :radio[name!=''], :checkbox[name!='']").each(function () {
                                let $this = $(this);
                                let fieldName = $this.attr('name');
                                if (!fieldName) return true; // Continue to the next iteration

                                if ($this.is(':radio') || $this.is(':checkbox')) {
                                    if ($this.is(':checked')) {
                                        fieldValues[fieldName] = $this.val();
                                    }
                                } else {
                                    fieldValues[fieldName] = $this.val();
                                }
                            })
                        }
                    });
                }

                // Validate
                let validationOutcome = options.validate(fieldValues);

                if (validationOutcome === true) {

                    // Calls callback, returns value.  If any value is returned, the modal will close and the promise will resolve.
                    return await callback(fieldValues);

                } else if (typeof validationOutcome === 'object' && validationOutcome !== null) {

                    _.apply_form_errors('.modal-body', validationOutcome);

                }
            },
            default: true
        });

        let $formElements = $('<div class="M-2" />')
            .addClass(options.center_fields ? 'form-elements-align-center' : 'form-elements-align-left');

        for (let label in options.fields) {
            let formGroup = $('<div/>', {
                class: 'mb-3'
            });

            // labels starting with _ are not rendered
            let formLabel = $('<label/>', {
                class: 'form-label',
                text: label.substring(0, 1) == '_' ? '' : label,
            });

            formGroup.append(formLabel);

            let formInput = options.fields[label];

            formGroup.append(formInput);

            // If the input group is hidden, then hide the whole group. 
            // Manipulation of the element must be done by .parent().hide/show() by the caller.
            if (formInput.style('display') == 'none') {
                formGroup.style('display', 'none');
                formInput.style('display', '');
            }

            $formElements.append(formGroup);
        }

        let clone = this.clone();
        let deferred = $.Deferred();
        clone.data('deferred', deferred);

        if (typeof options.body == "string") {
            options.body = $("<span></span>").text(options.body);
        }

        // Build body: Only include body wrapper if body is not empty
        let modalBody;
        
        // Check if we have body content
        let hasBodyContent = false;
        if (options.body) {
            if (typeof options.body === 'string') {
                hasBodyContent = options.body.trim() !== '';
            } else {
                hasBodyContent = true; // Non-string body content (jQuery objects, etc.)
            }
        }
        
        if (hasBodyContent) {
            // Create body wrapper and append body content
            modalBody = $('<div class="M-2"></div>').append(options.body);
            
            // Check if the body wrapper actually contains visible text
            if (modalBody.text().trim() === '') {
                // Body wrapper is empty (e.g., contains only empty spans), skip it
                modalBody = $formElements;
            } else {
                // Body has content, add form elements to it
                modalBody = modalBody.add($formElements);
            }
        } else {
            // No body content, use form elements only
            modalBody = $formElements;
        }

        clone.modal_show({
            title: options.title,
            body: modalBody,
            buttons: options.buttons,
            body_width: options.body_width
        });

        options.on_show();

        return deferred.promise();
    }

    // Reoopen a form and show an error (perhaps an ajax error)
    static form_reopen_error(error, hide_on_submit = false) {

        console.log(error);

        if ($("#modal-clone").is(":visible")) {
            // Immediately complete any existing fadeOut animations
            $("#modal-clone, #lp-modal-overlay").stop().show();
        } else {
            // Immediately complete any existing fadeOut animations
            $("#modal-clone, #lp-modal-overlay").stop().show();
        }

        // await _.sleep(300); // Add this line if needed

        $("#modal-clone").find('.async-pleasewait').each(function () {
            $(this).removeClass('async-pleasewait');
        });

        // Clear any previous form errors
        _.reset_form_errors('.modal-body');

        if (error.responseJSON && error.responseJSON.success == false && error.responseJSON.return) {
            // Apply the new form errors
            _.apply_form_errors('.modal-body', [error.responseJSON.return]);
        } else if (error.responseJSON && error.responseJSON.errors) {
            _.apply_form_errors('.modal-body', [error.responseJSON.errors]);
        } else if (error.responseJSON) {
            // Apply the new form errors
            _.apply_form_errors('.modal-body', error.responseJSON);
        } else {
            console.error(error);
            _.flash_alert(error, "danger");
        }

        let deferred = $.Deferred();
        $("#modal-clone").data('deferred', deferred);

        _.Modal._success_callback = (result) => {
            deferred.resolve(result);
            if (hide_on_submit) {
                _.Modal.hide();
            }
        };

        return deferred.promise();
    }

}